import {extend} from './utils';
import {config} from '../../config/config';
import {navigateToError} from '../../links';
import {authenticationService} from "../../webServices/authenticationService";

/**
 * Add `response` to Error as it helps debugging
 */
declare global {
  interface Error {
    response?: any;
  }
}

const checkStatus = function (response: Response) {
  if (response.status >= 200 && response.status < 300) {
    return response;
  } else {
    var error = new Error();
    error.response = response;
    throw error;
  }
}

const parseJson = function (response: Response): Promise<any> {
  return response.json();
}

const checkForRedirection = (error): never => {
  if (error.response && error.response.status === 401) {
    window.location.replace(config.portalConfig.portalLoginUrl);
  } else {
    navigateToError(error);
  }
  throw error;
};

/**
 * Gets the URL then checks the status and finally gives you the parsed JSON
 */
export const getJson = <T>(config: {
  url: string,
  headers?: any
}): Promise<T> => {
  const headers = extend({
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }, config.headers || {});
  return fetchWrapper(config.url, {
    method: 'GET',
    credentials: 'include',
    headers
  })
    .then(checkStatus)
    .then<T>(parseJson)
    .catch(checkForRedirection);
}

/**
 * Posts to the url with the
 * - right JSON headers
 * - serializes the request body
 * - checks the status of the response
 * - gives you the response as parsed JSON
 */
export const postJson = <T>(config: { url: string, headers?: any, body: any }): Promise<T> => {
  const headers = extend({
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }, config.headers || {});
  return fetchWrapper(config.url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(config.body)
  })
    .then(checkStatus)
    .then<T>(parseJson)
    .catch(checkForRedirection);
}
/**
 * Sends a DELETE to the url with the
 * - right JSON headers
 * - serializes the request body
 * - checks the status of the response
 * - gives you the response as parsed JSON
 */
export const deleteJson = <T>(config: { url: string, headers?: any, body: any }): Promise<T> => {
  const headers = extend({
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  }, config.headers || {});
  return fetchWrapper(config.url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
    body: JSON.stringify(config.body)
  })
    .then(checkStatus)
    .then<T>(parseJson)
    .catch(checkForRedirection);
}

// create type for options
type Options = {
  method: string,
  headers: any,
  credentials: any
  body?: any,
}

const fetchWrapper = async (url, options: Options): Promise<Response> => {
  const headers = {
    'Authorization': `Bearer ${await authenticationService.getAccessTokenSilently()}`,
    ...options.headers,
  };

  const newOptions = {
    ...options,
    headers,
  };

  return fetch(url, newOptions);
};
